import React from 'react';
import { Form, Button, Row, Col, Card, Alert, Modal, Collapse } from 'react-bootstrap';

import { render_critical_findings, split_into_paragraphs } from '../utils/CriticalFindings';

// on_ask_ai, when given, shows an Ask AI button that asks the AEO Assistant about this analysis. show_summary and
// set_show_summary collapse and expand the stage summaries, as the other sections' show flags do.
const AnalysisSummary = ({analysis_results, on_pdf_export, analyzing, fetching_pdf, on_ask_ai, show_summary, set_show_summary})=>{
	const scoring = (analysis_results && analysis_results.scoring) ? analysis_results.scoring : null;

	// { overall_score, grade }, which the backend computes and sends with the analysis.
	const analysis_grade = (analysis_results && analysis_results.analysis_grade) ? analysis_results.analysis_grade : null;

    if(!scoring){
        return;
    }

    const get_color_class = (v)=>{
        if(v <= 25){
            return "score-red";
        }else{
            if(v <= 50){
                return "score-orange";
            }else{
                if(v <= 75){
                    return "score-yellow";
                }else{
                    return "score-green";
                }
            }
        }
    };

	// The grade box is colored by the letter, so it always agrees with the grade bands the backend uses.
	const get_grade_color_class = (grade)=>{
		if(grade === 'D'){
			return "score-red";
		}

		if(grade === 'C'){
			return "score-orange";
		}

		if(grade === 'B'){
			return "score-yellow";
		}

		return "score-green";
	};

	// The grade box: the backend's grade, or the label alone until it has loaded.
	const render_grade_box = ()=>{
		if(!analysis_grade){
			return (
				<div className="grade-score-box">
					<div className="grade-label">Grade</div>
				</div>
			);
		}

		return (
			<div className={"grade-score-box " + get_grade_color_class(analysis_grade.grade)}>
				<div className="grade-label">Grade</div>
				{analysis_grade.grade}
			</div>
		);
	};

    const render_score_col = (label, value, desc)=>{
        return(
            <Col>
                {render_score(label, value, desc)}
            </Col>
        );
    };

    const render_score = (label, value, desc)=>{
        return(
            <div className={"score-box " + get_color_class(value)}>
                <div className="score-value">{Math.round(value) + "%"}</div>
                <p className="score-desc">{desc}</p>
            </div>
        );
    };

	// One heading per stage summary, in the order the PDF report prints them, each only when the analysis stored one,
	// its text in paragraphs of at most two sentences.
	const render_summary_paragraph = (heading, stage_result)=>{
		if(!stage_result || !stage_result.analysis_summary){
			return null;
		}

		return (
			<React.Fragment>
				<h4 className="stage-summary-heading">{heading}</h4>
				{split_into_paragraphs(stage_result.analysis_summary).map((paragraph, paragraph_index)=>(
					<p className="stage-summary-paragraph" key={paragraph_index}>{paragraph}</p>
				))}
			</React.Fragment>
		);
	};

	// The critical findings the analysis carries (critical_findings from the backend), shown first under the Summary
	// title.
	const render_critical_paragraphs = ()=>{
		return render_critical_findings(analysis_results.critical_findings);
	};

	// The stage summaries under a collapsible "Summary" title, like the other sections.
	const render_summary_paragraphs = ()=>{
		return (
			<Row>
				<Col className="analysis-summary-copy">
					<h3 className="summary-title"
						onClick={()=>set_show_summary(!show_summary)}
						style={{cursor:'pointer'}}>
						Summary {show_summary ? "▲" : "▼"}
					</h3>

					<Collapse in={show_summary}>
					<div>
						{render_critical_paragraphs()}
						{render_summary_paragraph("Answer Readiness", analysis_results.answer_readiness)}
						{render_summary_paragraph("Entity Clarity", analysis_results.entity_clarity)}
						{render_summary_paragraph("Content Depth", analysis_results.content_depth)}
						{render_summary_paragraph("Strengths", analysis_results.strengths)}
						{render_summary_paragraph("Weaknesses", analysis_results.weaknesses)}
					</div>
					</Collapse>
				</Col>
			</Row>
		);
	};

    const render_button_footer = (label, value, desc)=>{
        return(
            <span className='summary-button-footer'>
                {on_ask_ai ? (
                    <Button variant="primary"
                            type="button"
                            onClick={on_ask_ai}
                            className="analysis-summary-button ask-ai-button"
                            disabled={analyzing}>
                        Ask AI
                    </Button>
                ) : null}

                 <Button variant="primary" type="submit"
                         onClick={on_pdf_export}
                         className="analysis-summary-button pdf-export-button"
                         disabled={analyzing || fetching_pdf}>
                            PDF Export
                </Button>

                {/* Share is commented out until it does something.
                 <Button variant="primary"
                         type="submit"
                         className="analysis-summary-button share-button"
                         disabled={analyzing || fetching_pdf}>
                    Share
                </Button>
                */}
            </span>
        );
    };

    return(
        <Card className="analysis-results-card analysis-summary-card">
            <Row>
                <Col md={3} className="grade-col">
                    
                    {render_grade_box()}
                </Col>

                <Col md={9}>

                    <Row className="align-items-stretch">
                        {render_score_col(
                            "answer_readiness_score",
                            scoring.answer_readiness_score,
                            "How well the page can be used to answer queries."
                        )}
                        {render_score_col(
                            "semantic_clarity_score",
                            scoring.semantic_clarity_score,
                            "How clear the meaning and intent of the content is."
                        )}
                    </Row>

                    <Row>
                        {render_score_col(
                            "entity_clarity_score",
                            scoring.entity_clarity_score,
                            "How well entities and topics are defined."
                        )}
                        {render_score_col(
                            "content_depth_score",
                            scoring.content_depth_score,
                            "How deep and comprehensive the content is."
                        )}
                        {render_score_col(
                            "technical_seo_score",
                            scoring.technical_seo_score,
                            "How well the page follows technical SEO practices."
                        )}
                    </Row>

                    
                </Col>
            </Row>

            <Row>
                {render_button_footer()}
            </Row>

            {render_summary_paragraphs()}

        </Card>
    );
}

export default AnalysisSummary;